import { useMemo } from 'react';
import type { CSSProperties } from 'react';

/**
 * Generates CSS background-image with vertical lines for each ancestor level.
 * This helps give the tree a hierarchical depth, so users can easily see how many levels deep they are.
 *
 * @param {number} level The depth level of the current node
 * @param {number} indent The horizontal spacing per level, in pixels (default: 8px)
 * @param {CSSProperties.color} lineColor The color of the nesting lines (default: #E6E7EA - matches @fossaGray10)
 */
const useNestingLines = (
  level: number,
  indent = 8,
  lineColor = '#E6E7EA',
): CSSProperties => useMemo(() => {
  if (level === 0) {
    return {};
  }

  const lines = [];
  // Start from i = 1 to avoid drawing a line for the root node (level 0 position)
  for (let i = 1; i < level; i++) {
    const position = i * indent;
    lines.push(
      `linear-gradient(to right, transparent ${position}px, ${lineColor} ${position}px, ${lineColor} ${position + 1}px, transparent ${position + 1}px)`,
    );
  }

  return {
    backgroundImage: lines.join(', '),
    backgroundRepeat: 'no-repeat',
  };
}, [indent, level, lineColor]);

export default useNestingLines;
